/**
 * React binding for the pure ranking model. Any replacement interaction
 * (SPEC §7) can reuse this hook and render something completely different.
 */
import { useCallback, useMemo, useRef, useState } from 'react';
import type { PlayerId } from '@idgame/protocol';
import {
  createRankingModel,
  indexOf,
  moveTo,
  reconcileRankingModel,
  toRanking,
  type RankingModel,
} from './model.js';

export interface UseRankingModel {
  /** The current order, first place first. Always complete. */
  ranking: readonly PlayerId[];
  total: number;
  indexOf: (id: PlayerId) => number | null;
  moveTo: (id: PlayerId, index: number) => void;
}

export function useRankingModel(
  order: readonly PlayerId[],
  initialRanking?: readonly PlayerId[],
): UseRankingModel {
  const [model, setModel] = useState<RankingModel>(() =>
    createRankingModel(order, initialRanking),
  );

  // Roster changes mid-ranking (a late join, a reconnect snapshot) must not
  // strand the work already done, nor leave someone unrankable.
  const lastOrder = useRef(order);
  if (lastOrder.current !== order) {
    lastOrder.current = order;
    const next = reconcileRankingModel(model, order);
    if (next !== model) setModel(next);
  }

  const apply = useCallback((fn: (m: RankingModel) => RankingModel) => {
    setModel((current) => fn(current));
  }, []);

  return useMemo(
    () => ({
      ranking: toRanking(model),
      total: model.ranking.length,
      indexOf: (id) => indexOf(model, id),
      moveTo: (id, index) => apply((m) => moveTo(m, id, index)),
    }),
    [model, apply],
  );
}
